// @ts-nocheck
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import { assessmentActions, treatmentPlanActions } from '../../../../../store';
import {
  assessmentReviewSchema,
  assessmentSummarySchema,
  maladaptiveSchema,
} from '../../initialAssessment/utils/validationSchema';
import {
  ASSESSMENT_REVIEW_INITIAL_STATE,
  ASSESSMENT_SUMMARY_INITIAL_STATE,
} from '../../initialAssessment/constants/validationSchema';
import { getAllInvalidFieldNames } from '../../../../../helpers';
import { TreatmentPlanPages } from '../../constants/general';
import { PERSIST_DATA_STATES, STATE_IDS } from '../../initialAssessment/constants/states';
import { validateToolsData } from '../../initialAssessment/utils/validations';

import type {
  IInitialAssessment,
  IAssessmentTool,
  IAssessmentSummary,
} from '../../../../../utils/types';

function useValidateInitialAssessment() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const { selected: selectedClient } = useSelector((state: any) => state.clients);

  const {
    assessment,
    tools,
    summary,
    treatmentPlanId: currentTPId,
    persistDataState,
  } = useSelector((state: any) => state.assessments);
  const parentTreatmentPlanId = treatmentPlan?.parent_treatment_plan_id;
  const assessmentVersion = treatmentPlan?.resource_versions?.assessment;

  const [isValid, setIsValid] = useState<boolean>(false);
  const [fetchComplete, setFetchComplete] = useState<boolean>(false);

  useEffect(() => {
    if (!treatmentPlan?.id || !selectedClient?.id) return;
    dispatch(assessmentActions.updateState({ key: 'triggerValidationsOnPageLoad', data: true }));
    if (currentTPId !== treatmentPlan.id) {
      dispatch(assessmentActions.updateState({ key: 'persistDataState', data: {} }));
      dispatch(assessmentActions.updateState({ key: 'errorState', data: {} }));
    }
    if (currentTPId === treatmentPlan.id) {
      const initialAssessmentData = assessment?.id ? assessment : null;
      const toolsData = tools?.length > 0 ? tools : [];
      fetchAndValidateData(initialAssessmentData, toolsData, treatmentPlan.id);
    } else {
      fetchAndValidateData(null, [], treatmentPlan.id);
    }
    dispatch(assessmentActions.updateState({ key: 'treatmentPlanId', data: treatmentPlan.id }));
  }, [treatmentPlan?.id, selectedClient]);

  useEffect(() => {
    if (!fetchComplete) return;
    validate(assessment, tools, summary);
  }, [assessment, summary, tools, fetchComplete]);

  const fetchAndValidateData = async (
    initialAssemment: IInitialAssessment | null,
    tools: Array<IAssessmentTool>,
    treatmentPlanId: number,
  ) => {
    setFetchComplete(false);
    let assessmentData = initialAssemment;
    let toolsData = tools;
    if (!assessmentData?.id) {
      const { payload: assessment } = await dispatch(
        assessmentActions.getAssessment({ getAccessTokenSilently, planId: treatmentPlanId }),
      );
      assessmentData = assessment;
    }

    if (toolsData.length === 0) {
      if (!assessmentData?.id) {
        dispatch(assessmentActions.clearState());
      } else {
        const { payload: tools } = await dispatch(
          assessmentActions.getTools({
            getAccessTokenSilently,
            planId: treatmentPlanId,
            assessmentId: assessmentData.id,
          }),
        );
        toolsData = tools;
        const documentPromises = toolsData.map((tool: IAssessmentTool) => {
          return dispatch(
            assessmentActions.getToolDocuments({
              getAccessTokenSilently,
              planId: treatmentPlanId,
              assessmentId: assessmentData?.id,
              toolId: tool.id,
            }),
          );
        });
        await Promise.all(documentPromises);
      }
    }
    setFetchComplete(true);
  };

  const validate = (
    initialAssessment: IInitialAssessment,
    tools: Array<IAssessmentTool>,
    summary: IAssessmentSummary,
  ) => {
    const persistedAssessmentData =
      persistDataState?.[PERSIST_DATA_STATES[STATE_IDS.ASSESSMENT_REVIEW]];

    // Validate top level assessment data, is persisted top level assessment data exists validate it
    const data = persistedAssessmentData ? persistedAssessmentData : initialAssessment;
    const assessmentDataToValidate = {
      ...ASSESSMENT_REVIEW_INITIAL_STATE,
      ...data,
      parent_treatment_plan_id: parentTreatmentPlanId,
    };
    delete assessmentDataToValidate.id;
    delete assessmentDataToValidate.summary_observations_during_assessment;
    delete assessmentDataToValidate.summary_assessment_result;
    delete assessmentDataToValidate.summary_priorities_based_on_assessment;
    delete assessmentDataToValidate.date_of_clinical_interview_utc_offset;
    const assessmentErrors = getAllInvalidFieldNames(
      assessmentReviewSchema,
      assessmentDataToValidate,
    );

    // Validate assessment summary, if persisted summary exists for the TP validate it
    let summaryErrors = {};
    if (initialAssessment?.id) {
      const persistedSummary =
        persistDataState?.[PERSIST_DATA_STATES[STATE_IDS.ASSESMENT_SUMMARY]] || {};
      summaryErrors = getAllInvalidFieldNames(assessmentSummarySchema, {
        ...ASSESSMENT_SUMMARY_INITIAL_STATE,
        ...summary,
        ...persistedSummary,
      });
    }

    let errorCount = Object.keys(assessmentErrors).length;
    errorCount += Object.keys(summaryErrors).length;

    // Validate maladaptive behavior
    const persistedMaladaptiveKeys = Object.keys(persistDataState ?? {}).filter((key) =>
      key.includes(PERSIST_DATA_STATES[STATE_IDS.MALADAPTIVE] + '-'),
    );

    const persistedMaladaptive = persistedMaladaptiveKeys.flatMap((key) =>
      persistDataState[key] ? [persistDataState[key]] : [],
    );
    let persistedMaladaptiveErrorCount = 0;
    persistedMaladaptive.forEach((data) => {
      const errors = getAllInvalidFieldNames(maladaptiveSchema, data);
      delete errors.id;
      persistedMaladaptiveErrorCount += Object.keys(errors).length;
    });

    errorCount += persistedMaladaptiveErrorCount;

    // Validate if user has added at least one tool (Assessment Method section)
    // also validate if a persisted tool data exists, if yes, validate it.
    // Check if all tools has at least one visual and if persisted data exists
    // for any visual then validate persisted data
    const persistedNewTool =
      persistDataState?.[`${PERSIST_DATA_STATES[STATE_IDS.ASSESSMENT_METHOD]}-new`];
    if (initialAssessment?.id) {
      errorCount +=
        tools.length > 0 || persistedNewTool
          ? validateToolsData(tools, persistDataState, assessmentVersion)
          : 1;
    }
    setIsValid(errorCount === 0 ? true : false);

    dispatch(
      treatmentPlanActions.updateBadge({
        [TreatmentPlanPages.Assessment]: errorCount,
      }),
    );
  };

  return isValid;
}

export default useValidateInitialAssessment;
